import React from "react";
import ReactDOM from "react-dom";
import { Provider } from "react-redux";
import "./i18n";
import "./designs/style.css";
import App from "./App";
import * as serviceWorker from "./serviceWorker";
import store, { history } from "./store";
import { CookiesProvider } from "react-cookie";
import appConfig from "./constants/appConfig";
import * as Sentry from "@sentry/react";
import { FallbackOnUnexpectedError } from "./features/FallbackOnUnexpectedError";

type SentryIgnoredError = {
  name: string;
  message: string;
};

const sentryIgnoredErrors: SentryIgnoredError[] = [
  {
    name: "FirebaseError",
    message: "Failed to get document because the client is offline.",
  },
  {
    name: "TypeError",
    message: "Cannot read properties of null (reading 'V_')",
  },
  {
    name: "TypeError",
    message: "Cannot read properties of null (reading 'Zy')",
  },
  {
    name: "TypeError",
    message: "Cannot read properties of null (reading 'OO')",
  },
  {
    name: "TypeError",
    message: "Cannot read properties of null (reading 'getBoundingClientRect')",
  },
  {
    name: "Error",
    message:
      "UnknownError: Connection to Indexed Database server lost. Refresh the page to try again",
  },
  {
    name: "UnknownError",
    message:
      "Connection to Indexed Database server lost. Refresh the page to try again",
  },
  {
    name: "Error",
    message: "Network Error",
  },
  {
    name: "InvalidStateError",
    message:
      "Failed to execute 'transaction' on 'IDBDatabase': The database connection is closing.",
  },
  {
    name: "TypeError",
    message: "Cannot read properties of undefined (reading 'videojs')",
  },
  {
    name: "TypeError",
    message: "t is null",
  },
  {
    name: "TypeError",
    message: `null is not an object (evaluating 'document.getElementById("overlay-controller").classList')`,
  },
];

const isIgnoredError = (error: Error) => {
  return sentryIgnoredErrors.some(
    (ignored) =>
      error.name === ignored.name && error.message === ignored.message
  );
};

if (!appConfig.logDebug) {
  // DOM実装の console を置き換える
  const noop = () => {};
  window.console.log = noop;
  window.console.dir = noop;
  window.console.info = noop;
  window.console.warn = noop;
  window.console.debug = noop;
  window.console.error = noop;
  // sentry setting
  Sentry.init({
    /**
     * portalでsentryのログをあまり活用できていない、課金が大きいので一時的に無効にしていた
     * @see https://github.com/balus-co-ltd/spwn/issues/1437
     *
     * しかし、外形監視改善を行うため有効にする
     * @see https://github.com/balus-co-ltd/spwn/issues/2376
     */
    enabled: true,
    dsn: "https://605f6f470ea5488c967f38edf90a78cd@o503713.ingest.sentry.io/5598391",
    environment: process.env.NODE_ENV ?? "development",
    integrations: [
      Sentry.reactRouterV5BrowserTracingIntegration({
        history, // historyオブジェクトを直接渡す
      }),
    ],
    tracesSampleRate: process.env.NODE_ENV === "production" ? 0.05 : 1.0, // quota上限に達しないように本番環境では低めの値を設定する
    // notion: https://www.notion.so/balusco/sentry-1f998eee078b805c8991d0225ffc2e1a
    beforeSend(event, hint) {
      const error: unknown = hint.originalException;

      if (error instanceof Error) {
        if (isIgnoredError(error)) {
          return null;
        }
      }

      return event; // イベントを送信する場合はそのまま返す
    },
  });
}

// TransactionIDを実装時に再度有効にする
// Sentry.configureScope(scope => {
//   scope.setTag("Transaction ID", Math.random().toString(36).substr(2, 9));
// });

const rootElement = document.getElementById(appConfig.indexRoot);

if (rootElement === null) {
  // don't render
} else {
  ReactDOM.render(
    // sentry用のErrorBoundaryを追加
    // https://docs.sentry.io/platforms/javascript/guides/react/#add-error-boundary-components-react-19
    <Sentry.ErrorBoundary fallback={<FallbackOnUnexpectedError />}>
      <CookiesProvider>
        <Provider store={store}>
          <App history={history} />
        </Provider>
      </CookiesProvider>
    </Sentry.ErrorBoundary>,
    rootElement
  );
}

/**
 * /qrcode-redeemer パスでアクセスされた場合はサービスワーカーを有効化する
 */
if (window.location.pathname.startsWith("/qrcode-redeemer")) {
  serviceWorker.register();
  // prod環境でない場合には、firebase hosting functionでの書き換えが使用できないため
  // ここでmanifestを書き換える
  if (process.env.NODE_ENV !== "production") {
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) {
      manifestLink.setAttribute("href", `/manifest-qrcode-redeemer.json`);
    }
  }
} else {
  serviceWorker.unregister();
}
